import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  paymentsApi,
  type ApprovePaymentRequest,
  type PrintPaymentReceiptRequest,
  type ProcessRefundRequest,
  type RecordPaymentRequest,
  type ReconcilePaymentRequest,
  type RejectPaymentRequest,
} from "../services/paymentsApi";

export interface PaymentListParams {
  pageNumber: number;
  pageSize: number;
  search?: string;
  paymentMethod?: string;
  fromDate?: string;
  toDate?: string;
  view?: "All" | "PendingApproval" | "Unreconciled" | "Refunds";
  paymentType?: "Payment" | "Refund";
}

export const usePayments = (params: PaymentListParams, enabled = true) => {
  return useQuery({
    queryKey: ["payments", "list", params],
    queryFn: () => paymentsApi.getAllPayments(params),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const usePaymentByNumber = (paymentNumber: string) => {
  return useQuery({
    queryKey: ["payments", "by-number", paymentNumber],
    queryFn: () => paymentsApi.getPaymentByNumber(paymentNumber),
    enabled: !!paymentNumber,
  });
};

export const usePaymentsByOrderId = (orderId: string) => {
  return useQuery({
    queryKey: ["payments", "order", orderId],
    queryFn: () => paymentsApi.getPaymentsByOrderId(orderId),
    enabled: !!orderId,
  });
};

export const useUnreconciledPayments = (pageNumber = 1, pageSize = 20, enabled = true) => {
  return useQuery({
    queryKey: ["payments", "unreconciled", pageNumber, pageSize],
    queryFn: () => paymentsApi.getUnreconciledPayments(pageNumber, pageSize),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useRefundPayments = (enabled = true) => {
  return useQuery({
    queryKey: ["payments", "refunds"],
    queryFn: paymentsApi.getRefundPayments,
    enabled,
  });
};

export const useRecordPayment = () => {
  return useMutation({
    mutationFn: (payload: RecordPaymentRequest) => paymentsApi.recordPayment(payload),
    onSuccess: (payment) => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["payments", "order", payment.orderId] });
      queryClient.invalidateQueries({ queryKey: ["payments", "unreconciled"] });
      queryClient.invalidateQueries({ queryKey: ["payments", "refunds"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", payment.orderId] });
    },
  });
};

export const useReconcilePayment = () => {
  return useMutation({
    mutationFn: ({
      paymentId,
      payload,
    }: {
      paymentId: string;
      payload: ReconcilePaymentRequest;
    }) => paymentsApi.reconcilePayment(paymentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["payments", "unreconciled"] });
      queryClient.invalidateQueries({ queryKey: ["payments", "refunds"] });
    },
  });
};

export const useBulkReconcilePayments = () => {
  return useMutation({
    mutationFn: (paymentIds: string[]) => paymentsApi.bulkReconcilePayments(paymentIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["payments", "unreconciled"] });
      queryClient.invalidateQueries({ queryKey: ["payments", "refunds"] });
    },
  });
};

export const useProcessRefund = () => {
  return useMutation({
    mutationFn: (payload: ProcessRefundRequest) => paymentsApi.processRefund(payload),
    onSuccess: (payment) => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["payments", "order", payment.orderId] });
      queryClient.invalidateQueries({ queryKey: ["payments", "unreconciled"] });
      queryClient.invalidateQueries({ queryKey: ["payments", "refunds"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", payment.orderId] });
    },
  });
};

export const usePrintPaymentReceipt = () => {
  return useMutation({
    mutationFn: ({
      paymentId,
      payload,
    }: {
      paymentId: string;
      payload: PrintPaymentReceiptRequest;
    }) => paymentsApi.printReceipt(paymentId, payload),
  });
};

// ── Finance approval hooks ────────────────────────────────────────────────────

export const usePendingApprovalPayments = (pageNumber = 1, pageSize = 20, enabled = true) => {
  return useQuery({
    queryKey: ["payments", "pending-approval", pageNumber, pageSize],
    queryFn: () => paymentsApi.getPendingApprovalPayments(pageNumber, pageSize),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useApprovePayment = () => {
  return useMutation({
    mutationFn: ({
      paymentId,
      payload,
    }: {
      paymentId: string;
      payload: ApprovePaymentRequest;
    }) => paymentsApi.approvePayment(paymentId, payload),
    onSuccess: (payment) => {
      // Root query keys match their descendants, so one invalidation covers both
      // the pending-approval list and the selected order's payment query.
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      // Wallet settlement also updates its paired ledger entry. Refresh active
      // wallet screens in the background, without making approval wait for every
      // wallet query (including inactive screens).
      if (payment.paymentMethod?.toLowerCase() === "wallet") {
        void queryClient.invalidateQueries({ queryKey: ["wallets"] });
      }
    },
  });
};

export const useRejectPayment = () => {
  return useMutation({
    mutationFn: ({
      paymentId,
      payload,
    }: {
      paymentId: string;
      payload: RejectPaymentRequest;
    }) => paymentsApi.rejectPayment(paymentId, payload),
    onSuccess: (payment) => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      // Refresh active wallet screens in the background after the paired
      // pending debit is rejected.
      if (payment.paymentMethod?.toLowerCase() === "wallet") {
        void queryClient.invalidateQueries({ queryKey: ["wallets"] });
      }
    },
  });
};
